import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VerifiedIcon from '@mui/icons-material/VerifiedUser';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EventPayloadViewer from './eventPayloadViewer';
import { eventNameEquals } from '@/utils';

type Props = {
  eventData: any;
  maxHeight?: number;
  defaultExpanded?: boolean;
  isAppSource?: boolean;
  badgeLabel?: string;
  isSuperseded?: boolean;
  onDelete?: (eventData: any) => void;
};

const EventDataViewer: React.FC<Props> = ({
  eventData,
  maxHeight = 0,
  defaultExpanded = false,
  isAppSource = false,
  badgeLabel = 'App Source',
  isSuperseded = false,
  onDelete,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [showInputContext, setShowInputContext] = useState(false);

  if (!eventData) return null;

  const {
    sourceEntityType,
    sourceEntityDefName,
    sourceEntityName,
    eventPayload,
    inputEvent,
    dateTime,
    eventName,
    error,
  } = eventData;

  const label =
    sourceEntityName ||
    sourceEntityDefName ||
    (sourceEntityType ? t(`common.entityType.${sourceEntityType}`) : 'Event');
  const defName = sourceEntityDefName && sourceEntityDefName !== label ? sourceEntityDefName : undefined;
  const timeStr = dateTime ? new Date(dateTime).toLocaleTimeString() : '';
  const fullTimestamp = dateTime ? new Date(dateTime).toLocaleString() : '';

  const rawPayloadType =
    eventPayload && typeof eventPayload === 'object'
      ? eventPayload.payloadType || eventPayload.payload_type
      : undefined;

  const isError =
    rawPayloadType === 'ERROR' ||
    rawPayloadType === 'ERROR_STRING' ||
    Boolean(error) ||
    eventName?.toLowerCase().includes('error') ||
    eventName?.toLowerCase().includes('fail');

  return (
    <Accordion
      disableGutters
      variant="outlined"
      defaultExpanded={defaultExpanded || (isError && !isSuperseded)}
      sx={{
        borderRadius: '8px !important',
        overflow: 'hidden',
        border: 1,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        opacity: isSuperseded ? 0.7 : 1,
        borderColor: isError && !isSuperseded
          ? alpha(theme.palette.error.main, 0.4)
          : isAppSource
          ? alpha(theme.palette.primary.main, 0.3)
          : 'divider',
        bgcolor: isError && !isSuperseded
          ? alpha(theme.palette.error.main, 0.02)
          : isAppSource
          ? alpha(theme.palette.primary.main, 0.02)
          : 'transparent',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: '18px' }} />}
        sx={{
          minHeight: '38px !important',
          height: '38px',
          '& .MuiAccordionSummary-content': {
            margin: '0 !important',
            minWidth: 0,
            overflow: 'hidden',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            flexShrink: 0,
            ml: 0.5,
          },
          padding: '0 8px 0 12px',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%', minWidth: 0 }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, overflow: 'hidden' }}>
            {isError && <ErrorOutlineIcon color={isSuperseded ? 'disabled' : 'error'} sx={{ fontSize: '16px', flexShrink: 0 }} />}
            <Tooltip title={defName ? `${label} (${defName})` : label} arrow enterDelay={400}>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontWeight: isError || isAppSource ? 700 : 600,
                  fontSize: '13px',
                  color: isError && !isSuperseded ? 'error.main' : isAppSource ? 'primary.main' : 'text.primary',
                  minWidth: 0,
                }}
              >
                {label}
              </Typography>
            </Tooltip>
            {eventName &&
              !eventNameEquals(eventName, 'ItemProcessingComplete') &&
              !eventNameEquals(eventName, 'ItemGroupProcessingComplete') &&
              !eventNameEquals(eventName, 'ItemProcessingStarted') &&
              !eventNameEquals(eventName, label) && (
                <Tooltip title={`Event: ${eventName}`} arrow enterDelay={400}>
                  <Chip
                    label={eventName}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 18,
                      fontSize: '0.62rem',
                      fontWeight: 600,
                      borderColor: 'divider',
                      color: 'text.secondary',
                      maxWidth: 130,
                      flexShrink: 0,
                      '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                    }}
                  />
                </Tooltip>
              )}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, ml: 'auto' }}>
            {timeStr && (
              <Tooltip title={fullTimestamp} arrow enterDelay={400}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.disabled',
                    fontStyle: 'italic',
                    fontSize: '11px',
                    whiteSpace: 'nowrap',
                    cursor: 'default',
                  }}
                >
                  {timeStr}
                </Typography>
              </Tooltip>
            )}
            {isError ? (
              <Chip
                label={isSuperseded ? 'Resolved (Retry)' : 'Error'}
                size="small"
                color={isSuperseded ? 'default' : 'error'}
                sx={{ height: '18px', fontSize: '9px', fontWeight: 700, flexShrink: 0 }}
              />
            ) : (
              isAppSource && (
                <Chip
                  icon={<VerifiedIcon style={{ fontSize: '12px', color: theme.palette.primary.main }} />}
                  label={badgeLabel}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    height: '18px',
                    fontSize: '9px',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    flexShrink: 0,
                  }}
                />
              )
            )}
            {onDelete && (
              <Tooltip title="Delete this event record" arrow enterDelay={400}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(eventData);
                  }}
                  sx={{ p: 0.25, ml: 0.5 }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </AccordionSummary>
      <Divider />
      <AccordionDetails sx={{ p: 1.25, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Stack spacing={1} sx={{ width: '100%', maxWidth: '100%' }}>
          {/* PRIMARY: Output Payload */}
          <EventPayloadViewer
            payload={eventPayload ?? error}
            label="Output Payload"
            showMimeType={true}
            maxHeight={maxHeight > 0 ? maxHeight : 0}
            allowCopy={true}
          />

          {/* SECONDARY: Inbound Input Context (if available) */}
          {inputEvent && (
            <Box sx={{ width: '100%', maxWidth: '100%', border: '1px solid #e2e8f0', borderRadius: 1, bgcolor: '#fafafa' }}>
              <Box
                onClick={() => setShowInputContext(!showInputContext)}
                sx={{
                  px: 1,
                  py: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: '#f1f5f9' },
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', fontSize: '0.7rem' }}>
                  Inbound Input Context (Trigger / Prompt)
                </Typography>
                <IconButton size="small" sx={{ p: 0.25 }}>
                  {showInputContext ? <KeyboardArrowUpIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Box>
              <Collapse in={showInputContext} timeout="auto" unmountOnExit>
                <Box sx={{ p: 1, pt: 0.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                  <EventPayloadViewer
                    payload={inputEvent}
                    label="Inbound Input Context"
                    showMimeType={true}
                    maxHeight={maxHeight > 0 ? maxHeight : 0}
                    allowCopy={true}
                  />
                </Box>
              </Collapse>
            </Box>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default EventDataViewer;
